import {
	APPROVED_SCHEME_ATTACHMENT_LABEL_KEY,
	MAX_COURSE_ATTACHMENTS,
	MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH,
	OPTIONAL_COURSE_ATTACHMENT_LABELS,
} from "../constants/courseAttachments";
import {
	buildFileInfosFromObjectKeys,
	displayNameFromObjectKey,
	type AttachmentFileInfo,
} from "./buildFileInfosFromObjectKeys";

export type CourseAttachmentLabelRef = {
	uuid?: string;
	name: string;
	identifier?: string;
};

export type CourseLabeledAttachmentItem = {
	id: string;
	label: string;
	/** Display + payload label for required attachments from API catalog. */
	labelName?: string;
	labelUuid?: string;
	isPredefined: boolean;
	objectKey: string;
	fileInfo: AttachmentFileInfo | null;
};

export type CourseAttachmentApiValue =
	| string
	| {
			label?: string | null;
			objectKey?: string | null;
			filePath?: string | null;
			fileName?: string | null;
	  };

/** Labeled attachment metadata sent in create/update payload. */
export type CourseLabeledAttachmentPayload = {
	label: string;
	fileName: string;
};

function createAttachmentId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function applyRequiredLabelToItem(
	item: CourseLabeledAttachmentItem,
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem {
	if (!requiredLabel) return item;
	return {
		...item,
		label: requiredLabel.identifier ?? APPROVED_SCHEME_ATTACHMENT_LABEL_KEY,
		labelName: requiredLabel.name,
		labelUuid: requiredLabel.uuid,
		isPredefined: true,
	};
}

export function createDefaultAttachmentItem(
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem {
	return applyRequiredLabelToItem(
		{
			id: createAttachmentId(),
			label: APPROVED_SCHEME_ATTACHMENT_LABEL_KEY,
			isPredefined: true,
			objectKey: "",
			fileInfo: null,
		},
		requiredLabel,
	);
}

export function createAdditionalAttachmentItem(): CourseLabeledAttachmentItem {
	return {
		id: createAttachmentId(),
		label: "",
		isPredefined: false,
		objectKey: "",
		fileInfo: null,
	};
}

export function ensureDefaultAttachmentItem(
	items: CourseLabeledAttachmentItem[] | undefined,
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem[] {
	const list = items ?? [];
	const predefined = list.find(item => item.isPredefined);
	const extraPredefined = list.filter(item => item.isPredefined && item !== predefined);
	const others = list.filter(item => !item.isPredefined);

	if (predefined) {
		return [
			applyRequiredLabelToItem(predefined, requiredLabel),
			...extraPredefined.map(item => applyRequiredLabelToItem(item, requiredLabel)),
			...others,
		];
	}

	const [first, ...rest] = list;
	if (first?.objectKey) {
		return [
			applyRequiredLabelToItem(
				{
					...first,
					label: APPROVED_SCHEME_ATTACHMENT_LABEL_KEY,
					isPredefined: true,
				},
				requiredLabel,
			),
			...rest.map(item => ({ ...item, isPredefined: false })),
		];
	}

	return [createDefaultAttachmentItem(requiredLabel), ...list.map(item => ({ ...item, isPredefined: false }))];
}

function readObjectKey(value: CourseAttachmentApiValue): string {
	if (typeof value === "string") return value.trim();
	return (value.objectKey ?? value.filePath ?? value.fileName ?? "").trim();
}

function isLabeledAttachmentPayload(value: CourseAttachmentApiValue): value is CourseLabeledAttachmentPayload {
	return (
		typeof value === "object" &&
		value !== null &&
		typeof value.label === "string" &&
		typeof value.fileName === "string" &&
		value.fileName.trim().length > 0
	);
}

function isApprovedSchemeLabel(label: string, requiredLabel?: CourseAttachmentLabelRef): boolean {
	const trimmed = label.trim();
	if (requiredLabel && trimmed === requiredLabel.name.trim()) return true;
	return (
		trimmed === APPROVED_SCHEME_ATTACHMENT_LABEL_KEY ||
		trimmed.toLowerCase() === "approved scheme of studies"
	);
}

function buildFileInfo(objectKey: string, displayName?: string): AttachmentFileInfo {
	const name = displayName?.trim() || displayNameFromObjectKey(objectKey);
	return {
		...(buildFileInfosFromObjectKeys([objectKey])[0] ?? {
			objectKey,
			name,
			size: 0,
			type: "application/octet-stream",
		}),
		name,
	};
}

function canonicalizeOptionalAttachmentLabel(label: string): string {
	const trimmed = label.trim();
	const match = OPTIONAL_COURSE_ATTACHMENT_LABELS.find(
		option => option.toLowerCase() === trimmed.toLowerCase(),
	);
	return match ?? trimmed;
}

function labeledPayloadToItem(
	entry: CourseLabeledAttachmentPayload,
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem {
	const objectKey = entry.fileName.trim();
	const labelText = entry.label.trim();
	const isPredefined = isApprovedSchemeLabel(labelText, requiredLabel);

	return {
		id: createAttachmentId(),
		label: isPredefined
			? (requiredLabel?.identifier ?? APPROVED_SCHEME_ATTACHMENT_LABEL_KEY)
			: canonicalizeOptionalAttachmentLabel(labelText),
		labelName: isPredefined ? labelText || requiredLabel?.name : undefined,
		labelUuid: isPredefined ? requiredLabel?.uuid : undefined,
		isPredefined,
		objectKey,
		fileInfo: buildFileInfo(objectKey),
	};
}

function collectLabeledPayloadsFromCourse(course: CourseAttachmentContainer): CourseLabeledAttachmentPayload[] {
	const merged = [
		...(course.attachments ?? []),
		...(course.courseGroup?.attachments ?? []),
		...(course.courseGroup?.courseAttachments ?? []),
	];

	const result: CourseLabeledAttachmentPayload[] = [];
	const seen = new Set<string>();

	for (const value of merged) {
		if (isLabeledAttachmentPayload(value)) {
			const fileName = value.fileName.trim();
			const label = value.label.trim();
			if (!fileName || seen.has(fileName)) continue;
			seen.add(fileName);
			result.push({ label, fileName });
			continue;
		}

		const key = readObjectKey(value);
		if (!key || seen.has(key)) continue;
		seen.add(key);
		const legacyLabel =
			typeof value === "object" && value !== null && value.label?.trim() ? value.label.trim() : "";
		result.push({ label: legacyLabel, fileName: key });
	}

	return result;
}

export type CourseAttachmentContainer = {
	attachments?: CourseAttachmentApiValue[];
	courseGroup?: {
		attachments?: CourseAttachmentApiValue[];
		courseAttachments?: CourseAttachmentApiValue[];
	} | null;
};

function orderPredefinedAttachmentFirst(
	items: CourseLabeledAttachmentItem[],
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem[] {
	if (items.length === 0) return [createDefaultAttachmentItem(requiredLabel)];

	const predefinedIndex = items.findIndex(item => item.isPredefined);
	if (predefinedIndex <= 0) return items;

	const predefined = items[predefinedIndex];
	const others = items.filter((_, itemIndex) => itemIndex !== predefinedIndex);
	return [predefined, ...others];
}

export function parseCourseAttachmentsFromApi(
	raw: CourseAttachmentApiValue[] | undefined,
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem[] {
	const payloads = (raw ?? [])
		.map(value => {
			if (isLabeledAttachmentPayload(value)) {
				return { label: value.label.trim(), fileName: value.fileName.trim() };
			}
			const fileName = readObjectKey(value);
			if (!fileName) return null;
			const label =
				typeof value === "object" && value !== null && value.label?.trim() ? value.label.trim() : "";
			return { label, fileName };
		})
		.filter((entry): entry is CourseLabeledAttachmentPayload => entry != null);

	if (payloads.length === 0) {
		return [createDefaultAttachmentItem(requiredLabel)];
	}

	return ensureDefaultAttachmentItem(
		payloads.map(entry => labeledPayloadToItem(entry, requiredLabel)),
		requiredLabel,
	);
}

export function parseCourseAttachmentsFromCourse(
	course: CourseAttachmentContainer,
	requiredLabel?: CourseAttachmentLabelRef,
): CourseLabeledAttachmentItem[] {
	const labeledPayloads = collectLabeledPayloadsFromCourse(course);

	if (labeledPayloads.length > 0) {
		const hasExplicitLabels = labeledPayloads.some(entry => entry.label.trim().length > 0);
		const items = labeledPayloads.map((entry, index) => {
			const isPredefined =
				isApprovedSchemeLabel(entry.label, requiredLabel) ||
				(!hasExplicitLabels && index === 0) ||
				Boolean(requiredLabel && entry.label === requiredLabel.name);

			if (isPredefined) {
				return labeledPayloadToItem(
					{
						label: entry.label || requiredLabel?.name || "",
						fileName: entry.fileName,
					},
					requiredLabel,
				);
			}
			return labeledPayloadToItem(entry, requiredLabel);
		});

		return orderPredefinedAttachmentFirst(
			ensureDefaultAttachmentItem(items, requiredLabel),
			requiredLabel,
		);
	}

	return [createDefaultAttachmentItem(requiredLabel)];
}

export function collectAttachmentApiValues(course: CourseAttachmentContainer): CourseAttachmentApiValue[] {
	return collectLabeledPayloadsFromCourse(course).map(entry => ({
		label: entry.label,
		fileName: entry.fileName,
	}));
}

export function truncateAttachmentFileName(
	fileName: string,
	maxLength = MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH,
): string {
	const trimmed = fileName.trim();
	if (trimmed.length <= maxLength) return trimmed;

	const dotIndex = trimmed.lastIndexOf(".");
	const ext = dotIndex > 0 ? trimmed.slice(dotIndex) : "";
	const base = dotIndex > 0 ? trimmed.slice(0, dotIndex) : trimmed;
	const maxBase = Math.max(1, maxLength - ext.length);
	return `${base.slice(0, maxBase)}${ext}`;
}

export function sanitizeFileForUpload(
	file: File,
	maxNameLength = MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH,
): File {
	const nextName = truncateAttachmentFileName(file.name, maxNameLength);
	if (nextName === file.name) return file;
	return new File([file], nextName, { type: file.type, lastModified: file.lastModified });
}

export function resolveAttachmentUploadFileName(item: CourseLabeledAttachmentItem): string {
	const fromInfo = item.fileInfo?.name?.trim();
	if (fromInfo) return truncateAttachmentFileName(fromInfo);
	return truncateAttachmentFileName(displayNameFromObjectKey(item.objectKey));
}

export function normalizeAttachmentObjectKey(objectKey: string): string {
	return objectKey.trim();
}

export function isAttachmentObjectKeyValid(objectKey: string): boolean {
	const normalized = normalizeAttachmentObjectKey(objectKey);
	return normalized.length > 0 && normalized.length <= MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH;
}

export function isAttachmentUploadFileNameValid(item: CourseLabeledAttachmentItem): boolean {
	return resolveAttachmentUploadFileName(item).length <= MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH;
}

const APPROVED_SCHEME_FALLBACK_LABEL = "Approved Scheme of Studies";

function resolvePayloadLabel(item: CourseLabeledAttachmentItem): string {
	if (item.isPredefined) {
		const fromApi = item.labelName?.trim();
		if (fromApi) {
			return truncateAttachmentFileName(fromApi, MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH);
		}
		if (item.label.trim() && item.label !== APPROVED_SCHEME_ATTACHMENT_LABEL_KEY) {
			return truncateAttachmentFileName(item.label.trim(), MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH);
		}
		return truncateAttachmentFileName(APPROVED_SCHEME_FALLBACK_LABEL, MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH);
	}
	return truncateAttachmentFileName(item.label.trim(), MAX_COURSE_ATTACHMENT_FILE_NAME_LENGTH);
}

export function buildCourseCreateAttachmentsPayload(
	items: CourseLabeledAttachmentItem[],
): CourseLabeledAttachmentPayload[] {
	return items
		.filter(item => item.objectKey.trim().length > 0)
		.map(item => ({
			label: resolvePayloadLabel(item),
			fileName: normalizeAttachmentObjectKey(item.objectKey),
		}));
}

export function isAttachmentsStepValid(items: CourseLabeledAttachmentItem[] | undefined): boolean {
	const list = ensureDefaultAttachmentItem(items);
	if (list.length > MAX_COURSE_ATTACHMENTS) return false;

	const predefined = list.find(item => item.isPredefined);
	if (!predefined?.objectKey.trim()) return false;
	if (!isAttachmentObjectKeyValid(predefined.objectKey)) return false;
	if (!isAttachmentUploadFileNameValid(predefined)) return false;

	const additional = list.filter(item => !item.isPredefined);
	return additional.every(
		item =>
			item.label.trim().length > 0 &&
			item.objectKey.trim().length > 0 &&
			isAttachmentObjectKeyValid(item.objectKey) &&
			isAttachmentUploadFileNameValid(item),
	);
}

export function resolveAttachmentDisplayLabel(
	item: CourseLabeledAttachmentItem,
	t: (key: string) => string,
): string {
	if (item.labelName?.trim()) return item.labelName.trim();
	if (item.isPredefined || item.label === APPROVED_SCHEME_ATTACHMENT_LABEL_KEY) {
		return t("feature.courseBuilder.courses.steps.attachments.labels.approvedSchemeOfStudies");
	}
	return item.label.trim();
}

/** Select options for additional attachment labels (plus legacy value when editing). */
export function buildOptionalAttachmentLabelOptions(
	currentLabel?: string,
): Array<{ value: string; label: string }> {
	const options = OPTIONAL_COURSE_ATTACHMENT_LABELS.map(label => ({
		value: label,
		label,
	}));
	const trimmed = currentLabel?.trim() ?? "";
	if (!trimmed) return options;
	const alreadyListed = options.some(
		option => option.value.toLowerCase() === trimmed.toLowerCase(),
	);
	if (alreadyListed) return options;
	return [...options, { value: trimmed, label: trimmed }];
}

export function isOptionalAttachmentLabel(label: string): boolean {
	const trimmed = label.trim();
	if (!trimmed) return false;
	return OPTIONAL_COURSE_ATTACHMENT_LABELS.some(
		option => option.toLowerCase() === trimmed.toLowerCase(),
	);
}

/** @deprecated Use buildCourseCreateAttachmentsPayload */
export function buildCourseAttachmentPayload(
	items: CourseLabeledAttachmentItem[],
): CourseLabeledAttachmentPayload[] {
	return buildCourseCreateAttachmentsPayload(items);
}

/** @deprecated Use isAttachmentObjectKeyValid */
export function isAttachmentStorageKeyValid(objectKey: string): boolean {
	return isAttachmentObjectKeyValid(objectKey);
}
